/**
 * `Button`'s look (DESIGN_SYSTEM.md §4.1, §5.2): five variants, two sizes, `fill`, and the hover, focus,
 * press and disabled states. `root` is the `<button>` react-native-web emits, so it takes layout only
 * (§4.2); the paint is on `surface`.
 */
import type { ActionRole, Theme } from "@rista/ui/library/theme/theme";
import { defineVariants } from "@rista/ui/library/theme/variants";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost" | "link";

const filled = (role: (t: Theme) => ActionRole) => (t: Theme) => ({
  surface: { backgroundColor: role(t).bg, borderColor: role(t).border },
  label: { color: role(t).fg },
  icon: { color: role(t).fg },
});

/** a variant's ground under a pointer or a finger; `link` has none (it underlines instead) */
const groundOf = (t: Theme, variant: ButtonVariant | undefined, state: "hover" | "pressed") => {
  const action = variant === "primary" || variant === "danger" || variant === "secondary" ? t.color.action[variant] : null;
  if (action) return state === "hover" ? action.bgHover : action.bgPressed;
  return variant === "ghost" ? (state === "hover" ? t.color.surface.hover : t.color.surface.pressed) : undefined;
};

export const buttonLook = defineVariants({
  slots: ["root", "surface", "label", "icon"],
  base: (t) => ({
    // 🔴 a labelled action never shrinks: its label is one line, so shrinking only clips the verb (CLAUDE.md)
    root: { flexGrow: 0, flexShrink: 0, flexBasis: "auto" },
    surface: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: t.space.xs,
      borderRadius: t.radius.control,
      borderWidth: 1,
      borderColor: "transparent",
    },
    label: { ...t.type.bodyStrong, textAlign: "center" },
    icon: { color: t.color.text.primary },
  }),
  variants: {
    variant: {
      primary: filled((t) => t.color.action.primary),
      secondary: filled((t) => t.color.action.secondary),
      danger: filled((t) => t.color.action.danger),
      ghost: (t) => ({ label: { color: t.color.text.primary }, icon: { color: t.color.text.primary } }),
      link: (t) => ({ label: { color: t.color.text.link }, icon: { color: t.color.text.link } }),
    },
    size: {
      sm: (t) => ({ surface: { minHeight: 36, paddingHorizontal: t.space.lg, paddingVertical: t.space.sm } }),
      md: (t) => ({ surface: { minHeight: 44, paddingHorizontal: t.space.xxl, paddingVertical: t.space.md } }),
    },
    /** fill the width it is given: two equal halves of a dialog's footer */
    fill: { true: () => ({ root: { flexGrow: 1, flexShrink: 1 } }) },
  },
  compound: [
    // a text control: no ground, no border, no padding that would misalign it in a sentence
    { when: { variant: "link" }, style: () => ({ surface: { borderWidth: 0, paddingHorizontal: 0, paddingVertical: 0, minHeight: 0 } }) },
  ],
  states: {
    hovered: (t, v) =>
      v.variant === "link"
        ? { label: { textDecorationLine: "underline" } }
        : { surface: { backgroundColor: groundOf(t, v.variant as ButtonVariant, "hover") } },
    focused: (t, v) =>
      v.variant === "link" ? { label: { textDecorationLine: "underline" } } : { surface: { borderColor: t.color.border.focus } },
    pressed: (t, v) =>
      v.variant === "link" ? { label: { opacity: 0.75 } } : { surface: { backgroundColor: groundOf(t, v.variant as ButtonVariant, "pressed") } },
    disabled: () => ({ root: { opacity: 0.5 } }),
  },
  defaults: { variant: "secondary", size: "md" },
});
